import { useId, useState } from 'react';
import { DateTime } from 'luxon';

import { Time } from '../StrokeIcons';
import type { ButtonProps } from './Button';
import Button from './Button';
import CalendarRangePopover from './CalendarRangePopover';
import { cleanId } from './types';

export type DateRangeButtonProps = Omit<ButtonProps, 'children' | 'onClick' | 'type' | 'icon'> & {
  /** Range start timestamp in milliseconds */
  startValue?: number;
  /** Range end timestamp in milliseconds */
  endValue?: number;
  /** Called when a full range is selected, with both dates in "yyyy-MM-dd" format */
  onRangeChange?: (start: string, end: string) => void;
  /** Hide year when it matches the current year (e.g., "Apr 5" vs "Apr 5, 2024") */
  hideCurrentYear?: boolean;
  /** Placeholder text when no range is selected */
  placeholder?: string;
  /** Icon to display. Pass `null` to hide the icon. Defaults to calendar icon. */
  icon?: React.ReactNode | null;
};

/** Format a single timestamp, optionally dropping the year when it's the current year. */
function formatRangeEnd(ms: number, hideCurrentYear: boolean) {
  const dt = DateTime.fromMillis(ms);
  if (hideCurrentYear && dt.year === DateTime.now().year) {
    return dt.toFormat('MMM d');
  }
  return dt.toLocaleString(DateTime.DATE_MED);
}

export default function DateRangeButton({
  startValue,
  endValue,
  onRangeChange,
  hideCurrentYear = false,
  placeholder = 'Select date range',
  size = 'md',
  color,
  tone,
  icon,
  ...buttonProps
}: DateRangeButtonProps) {
  const id = cleanId(useId());
  const [isOpen, setIsOpen] = useState(false);
  const popoverId = `date-range-popover-${id}`;
  const anchorName = `--date-range-anchor-${id}`;

  // Format the range for display
  let displayText = placeholder;
  if (startValue && !isNaN(startValue) && endValue && !isNaN(endValue)) {
    displayText = `${formatRangeEnd(startValue, hideCurrentYear)} → ${formatRangeEnd(endValue, hideCurrentYear)}`;
  } else if (startValue && !isNaN(startValue)) {
    displayText = `${formatRangeEnd(startValue, hideCurrentYear)} → …`;
  }

  return (
    <>
      <Button
        size={size}
        color={color}
        tone={tone}
        inputStyle
        icon={icon === null ? undefined : (icon ?? <Time.Calendar />)}
        active={isOpen}
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        {...buttonProps}>
        {displayText}
      </Button>
      <CalendarRangePopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        color={color}
        tone={tone}
        startValue={startValue}
        endValue={endValue}
        onRangeChange={onRangeChange}
        onOpenChange={setIsOpen}
      />
    </>
  );
}
